import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CircleQuestionMark } from 'lucide-react'
import { useState } from 'react'
import { Copy, List } from '~/elements'
import { useCreateIntegrationApikey, useDeleteIntegrationApikey, useFeature, useSetIntegrationUrl } from '~/hooks'
import { integrationQueryOptions, listIntegrationApiKeysQueryOptions } from '~/queries'
import { friendlyFormatISODate } from '~/utils'
import { cn } from '~/utils/cn'

export const Route = createFileRoute('/workspaces/$workspaceId/integrations/$integrationId/settings')({
  component: Component,
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(listIntegrationApiKeysQueryOptions(params)),
})

function Component() {
  const { workspaceId, integrationId } = Route.useParams()

  const { data: integration } = useSuspenseQuery(integrationQueryOptions({ integrationId, workspaceId }))
  const { mutate: deleteIntegrationApikey } = useDeleteIntegrationApikey({ workspaceId, integrationId })
  const { mutate: createIntegrationApikey } = useCreateIntegrationApikey({ workspaceId, integrationId })
  const { data: apikeys } = useSuspenseQuery(listIntegrationApiKeysQueryOptions({ integrationId, workspaceId }))
  const { mutate: setIntegrationUrl, isError, isPending } = useSetIntegrationUrl({ workspaceId, integrationId })

  const canSelfHostIntegrations = useFeature('integrations.selfhosted')

  const currentUrl = integration?.url ?? ''

  const [url, setUrl] = useState(currentUrl)
  const [note, setNote] = useState('')

  const allowSelfHosted = !integration?.public

  return (
    <TooltipProvider>
      <div>
        {canSelfHostIntegrations && (
          <div>
            <div className="mb-4">
              <div className="mb-1 flex items-center text-sm text-gray-10">
                <p className="mr-1">{"Self hosted integration's url"}</p>
                {!allowSelfHosted && (
                  <Tooltip open={!allowSelfHosted ? undefined : false}>
                    <TooltipTrigger>
                      <CircleQuestionMark className="h-4 w-4 text-gray-10" />
                    </TooltipTrigger>
                    <TooltipContent>Self hosted is only allowed for private integrations</TooltipContent>
                  </Tooltip>
                )}
              </div>
              <div className="flex">
                <Input
                  className={cn('mr-2 w-full', {
                    'border-red-4 bg-red-3 text-red-11': isError,
                  })}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={integration?.public}
                  placeholder="https://yourdomain.com/integration"
                />
                {allowSelfHosted && url !== currentUrl && (
                  <Button
                    disabled={isPending}
                    className="flex-none cursor-pointer"
                    onClick={() => setIntegrationUrl({ url: url === '' ? null : url })}
                  >
                    <span>Save</span>
                  </Button>
                )}
              </div>
            </div>
            <div className="mb-2 flex items-end justify-between">
              <p className="text-sm text-gray-10">Integration Api Keys</p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button className="cursor-pointer">Create api key</Button>
                </PopoverTrigger>
                <PopoverContent align="end">
                  <div>
                    <p className="mb-1 text-sm text-gray-10">Note</p>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} className="mb-2" />
                    <div className="flex justify-end">
                      <Button
                        className="cursor-pointer"
                        onClick={() => {
                          createIntegrationApikey({ note })
                          setNote('')
                        }}
                      >
                        Create
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <List items={apikeys}>
              {(apikey) => <IakRow {...apikey} deleteIntegrationApikey={deleteIntegrationApikey} />}
            </List>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}

type IakRowProps = {
  note: string
  createdAt: string
  value?: string
  id: string
  deleteIntegrationApikey: (props: { id: string }) => void
}

const IakRow = ({ note, createdAt, value, deleteIntegrationApikey, id }: IakRowProps): JSX.Element => (
  <div className={cn('flex flex-wrap items-center justify-between p-4', { 'bg-grass-3': !!value })}>
    <div className="mr-3">
      <div className="mb-2">
        <b>{note || 'Integration Api Key'}</b>
      </div>
      <div className="text-sm capitalize text-gray-8">Created {friendlyFormatISODate(createdAt)}</div>
    </div>
    {value && (
      <div className="mr-3">
        <Copy className="max-w-[500px]" value={value} description="integration api key" />
      </div>
    )}
    <Button variant="destructive" onClick={() => deleteIntegrationApikey({ id })}>
      Delete
    </Button>
  </div>
)
